// Live visual-stability tracer for development.
//
// Two separate things are traced here, because the thing users complain about
// and the thing CLS measures are NOT the same set:
//
//   1. layout-shift entries — what CLS actually scores. Logged individually,
//      INCLUDING the ones the metric throws away (hadRecentInput), tagged so
//      you can tell "nothing moved" apart from "things moved but didn't count".
//
//   2. viewport/document size changes — what CLS deliberately IGNORES. The
//      spec excludes shifts caused by a viewport resize, and App Bridge v4
//      continuously resizes the embedded iframe to our content height. So a
//      resize feedback loop can make the whole app visibly judder while CLS
//      reports a clean 0. Without this tracer that failure mode is invisible.
//
// Also worth knowing when reading a 0: a layout shift requires an element that
// EXISTED in the previous frame to move. Replacing a skeleton with real
// content removes the old nodes and inserts brand-new ones, so a full swap can
// look jarring and still score exactly 0.
//
// Dev-only by default (wired in src/index.js). To trace a production build,
// run `localStorage.setItem('pmq:clsDebug', '1')` in the console and reload;
// remove the key to turn it off.

const rectStr = (r) =>
  r ? `${Math.round(r.x)},${Math.round(r.y)} ${Math.round(r.width)}x${Math.round(r.height)}` : '?';

function startLayoutShiftTracer() {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const sources = (entry.sources || []).map((source) => ({
          node: source.node,
          from: rectStr(source.previousRect),
          to: rectStr(source.currentRect),
        }));

        // Shifts within 500ms of discrete user input are excluded from CLS by
        // spec. Still logged — tagged — because an excluded shift is invisible
        // to the metric but perfectly visible to the merchant.
        const tag = entry.hadRecentInput ? 'shift (EXCLUDED: recent input)' : 'shift';

        // eslint-disable-next-line no-console
        console.log(
          `[CLS-debug] ${tag} ${entry.value.toFixed(4)} at ${Math.round(entry.startTime)}ms`,
          sources.length === 1 ? sources[0] : sources
        );
      }
    });

    // `buffered: true` replays shifts from before this ran, so the first boot
    // shifts are captured too.
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    // layout-shift isn't supported everywhere (Safari) — a tracer must never
    // take the app down.
  }
}

// The judder CLS can't see: our document's own height/width changing, which is
// what App Bridge v4 reacts to by resizing the iframe. A steady drip of these
// while the merchant only scrolls or moves the mouse means a resize feedback
// loop, not a normal content update.
function startResizeTracer() {
  if (typeof ResizeObserver === 'undefined') return;

  let last = { w: 0, h: 0 };
  let frame = null;
  let sinceLastCalm = 0;
  let calmTimer = null;

  const report = () => {
    frame = null;
    const el = document.documentElement;
    const w = Math.round(el.scrollWidth);
    const h = Math.round(el.scrollHeight);
    if (w === last.w && h === last.h) return;

    const overflowsX = el.scrollWidth > el.clientWidth;
    // eslint-disable-next-line no-console
    console.log(
      `[CLS-debug] document resize ${last.w}x${last.h} -> ${w}x${h}` +
        (overflowsX ? '  ⚠ HORIZONTAL OVERFLOW (this is what starts an iframe resize loop)' : '')
    );
    last = { w, h };

    // A burst of resizes with no content change is the signature of the loop.
    sinceLastCalm += 1;
    clearTimeout(calmTimer);
    calmTimer = setTimeout(() => {
      if (sinceLastCalm >= 5) {
        // eslint-disable-next-line no-console
        console.warn(
          `[CLS-debug] ${sinceLastCalm} document resizes in a burst — likely an iframe auto-resize feedback loop, not real content growth.`
        );
      }
      sinceLastCalm = 0;
    }, 1000);
  };

  try {
    const ro = new ResizeObserver(() => {
      // Coalesce to one measurement per frame; reading scrollHeight forces
      // layout, and a tracer must not become the jank it is measuring.
      if (frame === null) frame = window.requestAnimationFrame(report);
    });
    ro.observe(document.documentElement);
  } catch (e) {}
}

export function startClsDebug() {
  startLayoutShiftTracer();
  startResizeTracer();
}

export default startClsDebug;
